import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import Iconify from '../iconify';
import { PatientService } from '../../api/patient.service';
import { RecallsService } from '../../api/recalls.service';
import { useSnackbar } from '../../components/snackbar';
import { getApiErrorMessage } from '../../utils/util';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  onRecallCreated?: VoidFunction;
};

const RECALL_TYPES = [
  'Routine Eye Exam',
  'Contact Lens Aftercare',
  'Diabetic Screening',
  'Follow-Up',
  'Discount',
  'Promotion',
];

const COMMUNICATION_TYPES = ['SMS', 'Email', 'Phone Call'];

export default function SendRecallsDialog({
  open,
  onClose,
  onRecallCreated,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string } | null>(null);
  const [patientOptions, setPatientOptions] = useState<{ id: string; name: string }[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const [recallType, setRecallType] = useState<string>('Routine Eye Exam');
  const [communicationType, setCommunicationType] = useState<'SMS' | 'Email' | 'Phone Call'>('SMS');
  const [scheduledDate, setScheduledDate] = useState<Dayjs | null>(dayjs());
  const [message, setMessage] = useState<string>('');

  const patientService = new PatientService();
  const recallsService = new RecallsService();

  const handleFetchPatients = useCallback(async (search: string) => {
    if (!search || search.length < 2) return;
    setLoadingPatients(true);
    try {
      const response = await patientService.getAllPatients({ search });
      const patients = response.data.map((p: any) => ({
        id: p.id,
        name: `${p.firstName} ${p.surName || ''}`,
      }));
      setPatientOptions(patients);
    } catch (error) {
      console.error('Failed to fetch patients', error);
    } finally {
      setLoadingPatients(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedPatient(null);
      setScheduledDate(dayjs());
      setMessage('');
    }
  }, [open]);

  const handleSendRecall = async () => {
    if (!selectedPatient) {
      enqueueSnackbar('Please select a patient', { variant: 'error' });
      return;
    }

    if (!scheduledDate) {
      enqueueSnackbar('Please select a recall date', { variant: 'error' });
      return;
    }

    const today = dayjs().startOf('day');
    if (scheduledDate.startOf('day').isBefore(today)) {
      enqueueSnackbar('Recall date cannot be in the past', { variant: 'error' });
      return;
    }

    try {
      await recallsService.createRecall({
        patientId: selectedPatient.id,
        recallType,
        communicationType,
        scheduledDate: scheduledDate.toDate(),
        message: message || `Reminder: Your ${recallType} is due.`,
      });
      enqueueSnackbar('Recall created successfully', { variant: 'success' });
      onRecallCreated?.();
      onClose();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to create recall'), {
        variant: 'error',
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography
            variant="h3"
            component="div"
            sx={{ fontWeight: "600", textAlign: "center", mt: 2 }}
          >
            Create Manual Recall
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Patient Search</Typography>
              <Autocomplete
                fullWidth
                size="small"
                options={patientOptions}
                getOptionLabel={(option) => option.name}
                loading={loadingPatients}
                onInputChange={(e, value) => handleFetchPatients(value)}
                onChange={(e, value) => setSelectedPatient(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search by Name or ID"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingPatients ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <Typography variant="h6" sx={{ mb: 1 }}>Recall Type</Typography>
                <Select
                  value={recallType}
                  onChange={(e) => setRecallType(e.target.value)}
                >
                  {RECALL_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <Typography variant="h6" sx={{ mb: 1 }}>Communication Type</Typography>
                <Select
                  value={communicationType}
                  onChange={(e) => setCommunicationType(e.target.value as 'SMS' | 'Email' | 'Phone Call')}
                >
                  {COMMUNICATION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Scheduled Date</Typography>
              <DatePicker
                value={scheduledDate}
                onChange={(newValue) => setScheduledDate(newValue)}
                minDate={dayjs()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Custom Message (Optional)</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter a custom message or leave blank for default"
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={onClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSendRecall} variant="contained" color="primary">
            Create Recall
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}